import moment from "moment/min/moment-with-locales";
import { Chart as ChartJS, ArcElement, Tooltip, Legend, Title, SubTitle } from "chart.js";
import { Chart, getElementAtEvent } from "react-chartjs-2";
import annotationPlugin from "chartjs-plugin-annotation";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { useMemo, useRef } from "react";
import { toAutoDecimals } from "./HelperFunctions";
ChartJS.register(ArcElement, Tooltip, Legend, annotationPlugin, ChartDataLabels, Title, SubTitle);

export default function ContentGroupBalance({ data, percentageGrouping, contentGroups, setSelectedGroupId, radioName, subtitle, selectedContentGroups = [] }) {
    const chartRef = useRef();

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        borderRadius: 5,
        pointStyle: "rectRounded",
        plugins: {
            legend: {
                position: "bottom",
                labels: { usePointStyle: true },
            },
            title: {
                display: true,
                text: `Content Distribution | ${radioName}`,
                font: { size: 18 },
                padding: { bottom: 5 },
            },
            subtitle: {
                display: true,
                text: subtitle,
            },
            datalabels: {
                color: "#142533",
                font: { weight: "bold" },
                formatter: function (value, context) {
                    return context.datasetIndex === 0 ? `${Math.round(value)}%` : `${toAutoDecimals(value, 1, "")}`;
                },
            },
        },
        scales: {
            scores: {
                type: "linear",
                position: "right",
                max: 10,
                beginAtZero: true,
                grid: { display: false },
                border: { display: false },
            },
            percentage: {
                type: "linear",
                beginAtZero: true,
                grid: { display: false },
                border: { display: false },
            },
            x: {
                grid: { display: false },
                ticks: {
                    maxRotation: 0,
                    autoSkip: false,
                    callback: function (val) {
                        const thisList = this.getLabelForValue(val);
                        return thisList.map((x, i) => (i === 0 && x.length > 15 ? `${x.substring(0, 12)}... ` : x));
                    },
                },
            },
        },
        interaction: {
            mode: "index",
        },
    };

    const percentageKey = percentageGrouping === "breaks" ? "percentage_breaks" : "percentage_duration";
    data = data.filter(x => x[percentageKey] > 0.5);
    if (selectedContentGroups.length) {
        data = data.filter(x => selectedContentGroups.includes(x.content_group_id));
    }

    const hasScores = data.filter(x => x.score > 0)?.length;

    const chartData = useMemo(() => {
        if (data && contentGroups) {
            return {
                labels: data.map(x => [
                    contentGroups.find(g => g.group_id === x.content_group_id)?.display_name ?? "Other",
                    `${Math.round(moment.duration(x.duration, "seconds").asMinutes())} mins`,
                ]),
                datasets: [
                    {
                        type: "bar",
                        backgroundColor: ["#fcd76a"],
                        label: percentageGrouping === "breaks" ? "Breaks" : " Duration",
                        yAxisID: "percentage",
                        data: data.map(x => x[percentageKey]),
                    },
                ].concat(
                    hasScores
                        ? {
                              type: "bar",
                              backgroundColor: ["#7BA7CC"],
                              label: "Score",
                              yAxisID: "scores",
                              data: data.map(x => x.score),
                          }
                        : []
                ),
            };
        }
    }, [contentGroups, data, hasScores]);

    const onClick = event => {
        const element = getElementAtEvent(chartRef.current, event);
        if (element.length > 0) {
            const clickedGroupId = data[element[0].index].content_group_id;
            if (clickedGroupId > 0) {
                setSelectedGroupId(clickedGroupId);
            }
        }
    };

    return (
        <div className=" overflow-auto">
            <div className="content-testing-chart">
                <Chart ref={chartRef} data={chartData} options={chartOptions} onClick={onClick} />
            </div>
        </div>
    );
}
